import { cx } from 'cvb';
// eslint-disable-next-line import/no-extraneous-dependencies
import PropTypes from 'prop-types';
import constants from '../../../config/constants';
import _ from '../../../utils/LodashImports';
import {
  ALLOWED_CONFIGS_FOR_PRICE_BREAK,
  ALLOWED_CONFIGS_FOR_UNIT_PRICES,
  formatPriceRange,
} from '../../../utils/PriceHelpers';
import { getVariant } from '../../../utils/variant';
import DiscountBadge from '../../Badges/DiscountBadge/DiscountBadge';
import Fomo from '../../Fomo/Fomo';
import BodyCopy from '../../ui/BodyCopy/BodyCopy';
import { pricesStyles } from '../Prices.style';
import priceStylesConfig, {
  newPriceStylesConfig,
  newUnitPriceStyleConfig,
  oldPriceStyles,
  priceSizes,
  unitPriceStylesConfig,
} from '../PriceStyleConfig';

const Price = ({
  priceList,
  productId,
  variantId,
  page,
  regionCode,
  variant,
  isSponsoredProduct,
  isConnectProduct,
  operators,
  selectedOperatorId,
  appCtx,
  discountBadge,
  priceCount,
  labels,
  product,
  measurement,
  darSectionActive,
  showFomo,

  symbol,
  price,
  icons = '',
  label = '',
  type,
  crossed,
  unitPrice,
  pum = undefined,
  i,
}) => {
  const { deviceType, store } = appCtx;
  const isDesktop = deviceType === 'desktop';
  const isPDP = page === constants.PAGES.PDP;
  const { PRICE_TYPE } = constants;
  const highlitedPriceTypes = [PRICE_TYPE.internet, PRICE_TYPE.event];
  const currentVariant =
    (product && product.variants && product.variants.length && getVariant(product.variants, variantId)) || undefined;
  const comboPricesConfig = {
    perComboText: _.get(appCtx, 'siteConfig.textDictionary.PER_COMBO_TEXT', '/ Combo'),
    comboTotalPriceText: _.get(appCtx, 'siteConfig.textDictionary.COMBO_TOTAL_PRICE_TEXT', 'Total por separado'),
    comboPriceIndex: 2,
  };

  const shouldBreakFirstPrice = ALLOWED_CONFIGS_FOR_PRICE_BREAK.includes(variant);

  const pricesCount = (priceList || []).length;

  const isNormalPriceCrossedOut = _.get(appCtx, 'siteConfig.toggles.isNormalPriceCrossedOut', false);

  const isNewPriceApplicable = _.get(appCtx, 'siteConfig.toggles.isNewPriceApplicable', false);
  const showUnitPrice = !!ALLOWED_CONFIGS_FOR_UNIT_PRICES.includes(variant);
  const showUnitPriceXLP = showUnitPrice && !isPDP;
  const cmrLogoOnTop =
    page === constants.PAGES.PDP ||
    page === constants.PAGES.SLP ||
    page === constants.PAGES.PLP ||
    page === constants.PAGES.PLANS ||
    (!isSponsoredProduct && page === constants.PAGES.POD) ||
    ([page, variant].includes(constants.PAGES.CART) && isNewPriceApplicable);

  let classCMRContainer = 'cmr-icon-container';
  const hasTwoPricesPlanPage = !!(page === constants.PAGES.PLANS && priceList.length === 2);
  const effectiveRegionCode = regionCode === 'cl' && variant === 'CART' ? appCtx?.regionCode || regionCode : regionCode;
  const storeAndRegionCodeClassname = `${store || ''} ${effectiveRegionCode}`;
  const shouldShowFomo = showFomo && product?.variants?.length <= 1 && product?.variants?.[0]?.fomo?.enabled === true;
  const hasOffers = priceList && priceList.some ? priceList.some((p) => p.crossed) : false;
  const getPriceImportance = (icon, _crossed, isSep) => {
    if (icon && isSep) return '';

    if (icon === 'cmr-icon') {
      return 'high';
    }

    if (_crossed) {
      return isNewPriceApplicable ? 'medium' : 'low';
    }

    if (isNewPriceApplicable) {
      if (isSponsoredProduct) {
        return 'septenary';
      }
      return 'senary';
    }
    return '';
  };

  const getPriceConfig = (
    config,
    icon,
    _crossed = false,
    isSep = false,
    hasUnitStyles = false,
    pricePosition = 0,
    isComboPrice = false
  ) => {
    const priceConfig = hasUnitStyles ? newUnitPriceStyleConfig : newPriceStylesConfig;

    const variantPriceConfig = priceConfig[variant];
    const lineHeight = _.get(variantPriceConfig, `lineHeight[${pricePosition}]`, '');
    const lineHeightCrossed = _.get(variantPriceConfig, `lineHeight[${2}]`, '');
    const importance = getPriceImportance(icon, _crossed, isSep);
    if (isNormalPriceCrossedOut) {
      // **
      let priceSizeNotCrossed = config.size;

      if (!isComboPrice && priceList.length <= 2 && highlitedPriceTypes.includes(priceList[pricePosition].type)) {
        priceSizeNotCrossed = variant === 'CART' ? 'copy10' : config.size;
      }
      if (darSectionActive && variant !== 'CART') {
        priceSizeNotCrossed = 'copy15';
      }
      // **
      return {
        ...config,
        importance,
        size: isNewPriceApplicable && _crossed ? variantPriceConfig.crossed : priceSizeNotCrossed,
        lineHeight: isNewPriceApplicable && !_crossed ? lineHeight : lineHeightCrossed,
      };
    }
    return isNewPriceApplicable
      ? {
          ...config,
          importance,
          lineHeight,
        }
      : config;
  };

  const getUnitPrice = (_unitPrice, _symbol) => {
    if (!_unitPrice || !showUnitPrice) {
      return '';
    }
    const { price: _price, unit } = _unitPrice;
    return _price?.map((p) => `${_symbol} ${p} ${unit}`).join(' - ');
  };

  const getUnitForSale = (_unitPrice) => {
    if (!_unitPrice || !showUnitPrice) {
      return '';
    }
    const { unitForSale, unit } = _unitPrice;
    if (!isPDP) {
      return unit && typeof unit === 'string' ? unit.toLowerCase() : '';
    }
    return unitForSale && typeof unitForSale === 'string' ? unitForSale.toLowerCase() : '';
  };

  const getPrice = (_price, _symbol) => {
    if (!_price || !Array.isArray(_price)) {
      return '';
    }
    return _price?.map((p) => `${_symbol} ${p}`).join(' - ');
  };

  const getPriceAttributes = (_price, _type) => {
    const priceType = _type && _type.replace('Price', '');
    return { [`data-${priceType}-price`]: _price };
  };

  const connectOperatorName = () => {
    if (!isConnectProduct || !selectedOperatorId) return null;
    const operator = operators.find((op) => op.id === selectedOperatorId);
    if (operator)
      return (
        <p className="operator-name">
          ({operator.name})<style jsx>{pricesStyles}</style>
        </p>
      );
    return null;
  };

  const getPriceStyle = (hasUnitStyles = false) => {
    if (isNewPriceApplicable) {
      if (hasUnitStyles) {
        return newUnitPriceStyleConfig[variant][priceList.length && priceList[0].icons ? 'cmr' : 'noCmr'];
      }
      return newPriceStylesConfig[variant][pricesCount && priceList[0].icons ? 'cmr' : 'noCmr'];
    }

    if (isNormalPriceCrossedOut) {
      if (hasUnitStyles) {
        return unitPriceStylesConfig[variant][priceList.length && priceList[0].icons ? 'cmr' : 'noCmr'];
      }
      return priceStylesConfig[variant][pricesCount && priceList[0].icons ? 'cmr' : 'noCmr'];
    }

    return oldPriceStyles[variant];
  };

  const priceConfig = (hasUnitStyles) => getPriceStyle(hasUnitStyles);

  const shouldRenderPumUnitForSale = variant !== 'CART' || !isDesktop;
  const isCmr = icons.length > 0 && icons === 'cmr-icon';
  const unitPriceValue = getUnitPrice(unitPrice, symbol);
  const unitForSale = getUnitForSale(unitPrice);
  const hasUnitStyles = !!showUnitPrice && !!unitPriceValue && !currentVariant?.comboData;
  classCMRContainer =
    hasTwoPricesPlanPage && isCmr ? `${classCMRContainer} cmr-icon-container-two-prices` : classCMRContainer;
  const isFirstPrice = i === 0;
  const isFirstPriceFormatNeeded =
    isFirstPrice && shouldBreakFirstPrice && typeof price === 'string' && price.length > 1;
  const priceToRender = showUnitPriceXLP && unitPrice ? unitPrice.price : price;
  const pricesToRender = isFirstPriceFormatNeeded
    ? formatPriceRange(variant, priceToRender, pricesCount, isCmr, symbol.trim())
    : [`${getPrice(priceToRender, symbol)} ${!label ? '' : label} ${priceConfig(hasUnitStyles)[i].size || ''}`];
  if (crossed && currentVariant?.comboData?.showTotalPrice) {
    return null;
  }
  const renderPumUnitForSale = (pumUnitForSale) => {
    if (!pumUnitForSale) {
      return null;
    }
    return (
      <div className="pum-unitforsale">
        <span>{`/ ${pumUnitForSale}`}</span>
      </div>
    );
  };
  const renderPumPrice = (pumobject) => {
    const { symbol: pumSymbol, label: pumLabel, price: pumPrice } = pumobject;
    if (appCtx.regionCode !== 'co') {
      return (
        <div className="pum-price generic">
          <span>{`(${pumSymbol} ${pumPrice} ${labels.PUM_AFFIX} ${pumLabel})`}</span>
          <style jsx>{pricesStyles}</style>
        </div>
      );
    }
    return (
      <div className="pum-price">
        <span>{`(${pumLabel} ${labels.PUM_AFFIX} ${pumSymbol} ${pumPrice})`}</span>
        <style jsx>{pricesStyles}</style>
      </div>
    );
  };
  return (
    <li
      {...getPriceAttributes(price, type)}
      key={priceToRender}
      className={cx(
        `prices-${i}`,
        hasUnitStyles && isPDP && 'has-unit-price',
        isNewPriceApplicable && deviceType === constants.DEVICE_MOBILE && variant === constants.PAGES.CART && 'mb-2px'
      )}
    >
      <div className={classCMRContainer} data-variant={variant}>
        {cmrLogoOnTop && icons.length > 0 && icons === 'cmr-icon' && (
          <i
            className={cx(
              `unica-cmr-icon pdp-icon ${storeAndRegionCodeClassname}`,
              isNewPriceApplicable && isPDP && 'big-icon',
              isNewPriceApplicable &&
                deviceType === constants.DEVICE_MOBILE &&
                variant === constants.PAGES.CART &&
                'cmr-icon-small',
              priceCount && priceCount === 1 && 'pdp-connect-tab-plan'
            )}
          />
        )}
        {hasUnitStyles && isPDP && (
          <>
            <BodyCopy
              {...getPriceConfig(priceSizes[priceConfig(false)][i], icons, crossed, false, false, i)}
              crossed={crossed}
            >
              {unitPriceValue}
            </BodyCopy>
            <BodyCopy
              {...getPriceConfig(priceSizes[priceConfig(true)][i], icons, crossed, true, false, i)}
              crossed={false}
            >
              &nbsp;|&nbsp;
            </BodyCopy>
          </>
        )}
        {pricesToRender.map((p, priceToRenderIndex) => (
          <BodyCopy
            key={`${productId}-${p}`}
            isHighlighted={highlitedPriceTypes.includes(type)}
            {...getPriceConfig(priceSizes[priceConfig(hasUnitStyles)][i], icons, crossed, false, hasUnitStyles, i)}
            crossed={crossed}
            width={isFirstPriceFormatNeeded && pricesToRender.length > 1 && priceToRenderIndex === 0 ? 'width-100' : ''}
          >
            {p}
            {currentVariant?.comboData ? comboPricesConfig.perComboText : ''}
            {hasUnitStyles && !isPDP && <>&nbsp;{unitForSale}</>}
          </BodyCopy>
        ))}
        {shouldShowFomo && hasOffers && !measurement && (crossed || (!priceList.some((p) => p.crossed) && i === 0)) && (
          <div className="fomo-box">
            <Fomo product={product} />
          </div>
        )}
        {hasUnitStyles && isPDP && (
          <BodyCopy
            {...getPriceConfig(priceSizes[priceConfig(hasUnitStyles)][i], icons, crossed, false, hasUnitStyles, i)}
            crossed={crossed}
          >
            &nbsp;
            {unitForSale}
          </BodyCopy>
        )}
        {!cmrLogoOnTop && isCmr && (
          <i
            className={cx(`unica-cmr-icon ${storeAndRegionCodeClassname}`, isNewPriceApplicable && isPDP && 'big-icon')}
          />
        )}
        {isFirstPrice && isConnectProduct && (
          <div className={isCmr ? 'margin-left-6' : ''}>{connectOperatorName()}</div>
        )}
        {shouldRenderPumUnitForSale &&
          !unitForSale && // to not duplicate with sodimac m2 unit for sale development
          currentVariant &&
          currentVariant.unitForSale &&
          !currentVariant?.comboData &&
          renderPumUnitForSale(currentVariant.unitForSale)}
        {/* tottus measurement unit */}
        {measurement && <span className={`${type} tocom-unit`}>{measurement['unidad-de-medida']}</span>}
        {isFirstPrice && discountBadge && <DiscountBadge badge={discountBadge} />}
        {shouldShowFomo && measurement && hasOffers && (crossed || (!priceList.some((p) => p.crossed) && i === 0)) && (
          <div className="fomo-box">
            <Fomo product={product} />
          </div>
        )}
        <div className="price-extras">{pum && renderPumPrice(pum)}</div>
      </div>
      <style jsx>{pricesStyles}</style>
    </li>
  );
};

Price.defaultProps = {
  productId: '',
  variantId: '',
  page: 'POD',
  regionCode: 'cl',
  variant: 'PDP_MAIN',
  labels: {},
  operators: [],
  selectedOperatorId: undefined,
  isConnectProduct: false,
  isSponsoredProduct: false,
  appCtx: {},
  discountBadge: undefined,
  priceCount: undefined,
  product: {},
  measurement: undefined,
  darSectionActive: false,
  showFomo: false,

  symbol: '',
  price: [],
  icons: '',
  label: '',
  type: '',
  crossed: false,
  unitPrice: undefined,
  pum: undefined,
  i: 0,
};

Price.propTypes = {
  priceList: PropTypes.arrayOf(PropTypes.object).isRequired,
  productId: PropTypes.string,
  variantId: PropTypes.string,
  page: PropTypes.oneOf(['POD', 'PDP', 'CART', 'SLP', 'PLANS']),
  regionCode: PropTypes.string,
  variant: PropTypes.string,
  labels: PropTypes.object,
  isSponsoredProduct: PropTypes.bool,
  isConnectProduct: PropTypes.bool,
  operators: PropTypes.arrayOf(PropTypes.object),
  selectedOperatorId: PropTypes.string,
  appCtx: PropTypes.object,
  discountBadge: PropTypes.object,
  priceCount: PropTypes.number,
  product: PropTypes.object,
  measurement: PropTypes.object,
  darSectionActive: PropTypes.bool,
  showFomo: PropTypes.bool,

  symbol: PropTypes.string,
  price: PropTypes.arrayOf(PropTypes.string),
  icons: PropTypes.string,
  label: PropTypes.string,
  type: PropTypes.string,
  crossed: PropTypes.bool,
  unitPrice: PropTypes.object,
  pum: PropTypes.any,
  i: PropTypes.number,
};

export default Price;
export { Price };
